// src/admin/hooks/usePostForm.js

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { postsService } from "../services/postsService";

export default function usePostForm({
  mode = "create", 
  postId = null, 
  initialValues = {},
} = {}) {

  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [imagePreview, setImagePreview] = useState(initialValues.featured_image || null);
  const [ogImagePreview, setOgImagePreview] = useState(initialValues.og_image || null);

  const [formData, setFormData] = useState({
    title: "",
    excerpt: "",
    content: "",
    category: "",
    tags: [],
    status: "draft",
    featured_image: null,

    // SEO
    meta_title: "",
    meta_description: "",
    keywords: "",
    canonical_url: "",
    og_title: "",
    og_description: "",
    og_image: null,

    ...initialValues,
  });

  // Load post data when editing
  useEffect(() => {
    if (mode !== "edit" || !postId) {
      return;
    }

    const loadPost = async () => {
      try {
        const post = await postsService.getPost(postId);
        
        console.log("📝 Loaded post for editing:", post);
        console.log("📄 Content HTML length:", post.content?.length);
        console.log("📋 Category from API:", post.category);
        console.log("📋 Category ID:", post.category?.id);
        console.log("📋 Category ID type:", typeof post.category?.id);

        // Handle category properly - could be an object or just an ID
        let categoryId = "";
        if (post.category) {
          // If category is an object with an id
          if (typeof post.category === 'object' && post.category.id) {
            categoryId = post.category.id;
          } 
          // If category is already a string or number
          else if (typeof post.category === 'string' || typeof post.category === 'number') {
            categoryId = post.category;
          }
        }

        console.log("📋 Category ID to set:", categoryId);

        const newFormData = {
          title: post.title || "",
          excerpt: post.excerpt || "",
          content: post.content || "",
          category: categoryId, // Use the extracted category ID
          tags: post.tags || [],
          status: post.status || "draft",

          // SEO - handle both nested and flat structures
          meta_title: post.seo?.meta_title || post.meta_title || "",
          meta_description: post.seo?.meta_description || post.meta_description || "",
          keywords: post.seo?.keywords || post.keywords || "",
          canonical_url: post.seo?.canonical_url || post.canonical_url || "",
          og_title: post.seo?.og_title || post.og_title || "",
          og_description: post.seo?.og_description || post.og_description || "",
          og_image: null,

          featured_image: null,
        };

        console.log("📝 New form data:", newFormData);
        console.log("📋 Category value being set:", newFormData.category);

        setFormData(newFormData);
        setImagePreview(post.featured_image);
        setOgImagePreview(post.seo?.og_image || post.og_image || null);

      } catch (err) {
        console.error("❌ Failed to load post:", err);
        setErrors({ form: "Failed to load post data" });
      }
    };

    loadPost();
  }, [mode, postId]);

  const updateField = (field, value) => {
    console.log(`🔄 Updating field: ${field} =`, value);
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleImageChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    updateField("featured_image", file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleOgImageChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    updateField("og_image", file);
    setOgImagePreview(URL.createObjectURL(file));
  };

  const buildPayload = () => {
    const payload = new FormData();

    // Basic fields
    payload.append("title", formData.title || "");
    payload.append("excerpt", formData.excerpt || "");
    payload.append("content", formData.content || "");
    payload.append("category", formData.category || "");
    payload.append("status", formData.status || "draft");

    // SEO fields
    payload.append("meta_title", formData.meta_title || "");
    payload.append("meta_description", formData.meta_description || "");
    payload.append("keywords", formData.keywords || "");
    payload.append("canonical_url", formData.canonical_url || "");
    payload.append("og_title", formData.og_title || "");
    payload.append("og_description", formData.og_description || "");

    // Featured image - only append if it's a new File
    if (formData.featured_image instanceof File) {
      payload.append("featured_image", formData.featured_image);
    }

    // OG image - only append if it's a new File
    if (formData.og_image instanceof File) {
      payload.append("og_image", formData.og_image);
    }

    // Tags
    if (formData.tags && formData.tags.length > 0) {
      formData.tags.forEach(tag => {
        payload.append("tag_ids", tag.id || tag);
      });
    }

    console.log("📤 Building payload:");
    console.log("Category being sent:", formData.category);
    console.log("Content type:", typeof formData.content);
    console.log("Content length:", formData.content?.length);
    console.log("Contains HTML:", formData.content?.includes("<") ? "✅ Yes" : "❌ No");

    return payload;
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.title?.trim()) {
      newErrors.title = "Title is required.";
    }

    if (!formData.content?.trim()) {
      newErrors.content = "Content is required.";
    }

    if (!formData.category) {
      newErrors.category = "Select a category.";
    }

    console.log("🔍 Form Data:", formData);
    console.log("🔍 Category value:", formData.category);
    console.log("🔍 Validation Errors:", newErrors);

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const submit = async () => {
    if (!validate()) return;

    try {
      setSaving(true);
      const payload = buildPayload();

      console.log(`🚀 ${mode === "create" ? "Creating" : "Updating"} post...`);
      console.log("📤 Category in payload:", formData.category);

      if (mode === "create") {
        await postsService.createPost(payload);
      } else {
        await postsService.updatePost(postId, payload);
      }

      navigate("/admin/posts");
    } catch (error) {
      console.error("❌ Failed to save post:", error);
      
      if (error.response?.data) {
        console.error("Server errors:", error.response.data);
        setErrors({ form: error.response.data.message || "Failed to save post" });
      }
    } finally {
      setSaving(false);
    }
  };

  return {
    formData,
    updateField,
    imagePreview,
    handleImageChange,
    ogImagePreview,
    handleOgImageChange,
    submit,
    saving,
    errors,
    setErrors,
  };
}